/**
 * FetchController class to manage multiple AbortController instances.
 */
class FetchController {
  /**
   * Creates an instance of FetchController.
   */
  constructor() {
    this.controllers = new Map()
  }

  /**
   * Checks if an AbortController exists for the given key.
   * @param {string} key - The key to check.
   * @returns {boolean} - True if the controller exists, false otherwise.
   */
  has(key) {
    return this.controllers.has(key)
  }

  /**
   * Creates a new AbortController for the given key if it doesn't already exist.
   * @param {string} key - The key for the new AbortController.
   * @returns {AbortSignal} - The signal of the created or existing AbortController.
   */
  create(key) {
    if (this.has(key)) {
      return this.controllers.get(key).signal
    }
    const controller = new AbortController()
    this.controllers.set(key, controller)
    return controller.signal
  }

  /**
   * Aborts the AbortController associated with the given key and removes it from the map.
   * @param {string} key - The key of the AbortController to abort.
   */
  abort(key) {
    if (this.has(key)) {
      this.controllers.get(key).abort('canceled')
      this.controllers.delete(key)
    }
  }

  /**
   * Aborts multiple AbortControllers based on the provided list of keys.
   * @param {string[]} keylist - The list of keys for the AbortControllers to abort.
   */
  abortSome(keylist = []) {
    keylist.forEach((key) => this.abort(key))
  }
}

export const controller = new FetchController()

// Usage example:
// const signal = controller.create('request1');
// fetch(url, { signal }).catch(err => console.error('Fetch aborted:', err));
// controller.abort('request1');
// controller.abortSome();
